import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Wallet, Package, Users, Network, GitBranch, Sparkles,
  ArrowDownToLine, ReceiptText, Settings, LogOut, Menu, X, ShieldCheck,
  CheckSquare, UsersRound, BarChart3, SlidersHorizontal, Hexagon, Gem,
} from "lucide-react";

const userNav = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/packages", label: "Packages", icon: Package },
  { to: "/app/wallets", label: "Wallets", icon: Wallet },
  { to: "/app/referrals", label: "Referrals", icon: Users },
  { to: "/app/team", label: "Team Tree", icon: GitBranch },
  { to: "/app/cards", label: "Magic Cards", icon: Sparkles },
  { to: "/app/silver", label: "Silver Card", icon: Gem },
  { to: "/app/withdraw", label: "Withdraw", icon: ArrowDownToLine },
  { to: "/app/transactions", label: "Transactions", icon: ReceiptText },
  { to: "/app/account", label: "Account", icon: Settings },
];

const adminNav = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/approvals", label: "Approvals", icon: CheckSquare },
  { to: "/admin/withdrawals", label: "Withdrawals", icon: ArrowDownToLine },
  { to: "/admin/users", label: "Users", icon: UsersRound },
  { to: "/admin/user-transactions", label: "User Transactions", icon: ReceiptText },
  { to: "/admin/magic-cards", label: "Magic Cards", icon: Sparkles },
  { to: "/admin/silver-cards", label: "Silver Card", icon: Gem },
  { to: "/admin/reports", label: "Reports & Ledger", icon: BarChart3 },
  { to: "/admin/settings", label: "Business Rules", icon: SlidersHorizontal },
];

export default function AppShell({ children, admin = false }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const items = admin ? adminNav : userNav;

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2.5 px-5 h-16 border-b border-white/5">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500">
          <Hexagon className="h-5 w-5 text-slate-900" />
        </span>
        <div className="leading-tight">
          <div className="font-display font-extrabold text-white text-[0.95rem]">Rightwayp2p</div>
          <div className="text-[0.6rem] uppercase tracking-[0.2em] text-emerald-400">{admin ? "Command Center" : "Matrix Wallet"}</div>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {items.map((it) => (
          <NavLink
            key={it.to}
            to={it.to}
            end={it.end}
            onClick={() => setOpen(false)}
            data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                isActive
                  ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                  : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
              }`
            }
          >
            <it.icon className="h-[1.05rem] w-[1.05rem]" />
            {it.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-white/5">
        {!admin && user?.role === "user" && (
          <div className="px-3 py-2 mb-2 rounded-xl bg-white/5 text-xs">
            <div className="text-slate-400">Referral code</div>
            <div className="font-num text-emerald-300 font-semibold">{user?.referral_code}</div>
          </div>
        )}
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="flex w-full items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-400 hover:text-rose-300 hover:bg-rose-500/10 transition-all"
        >
          <LogOut className="h-[1.05rem] w-[1.05rem]" /> Logout
        </button>
      </div>
    </div>
  );

  return (
    <div className="app-bg min-h-screen">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 glass border-r border-white/5 z-30">
        {SidebarInner}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 glass border-r border-white/5">{SidebarInner}</aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 h-16 glass border-b border-white/5 flex items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-300" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            {admin && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-300 bg-amber-500/10 border border-amber-500/30 px-2.5 py-1 rounded-full">
                <ShieldCheck className="h-3.5 w-3.5" /> Admin
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right leading-tight hidden sm:block">
              <div className="text-sm font-semibold text-white">{user?.name}</div>
              <div className="text-xs text-slate-500">{user?.email}</div>
            </div>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-cyan-500 text-slate-900 font-bold text-sm">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </span>
          </div>
        </header>
        <main className="px-4 sm:px-8 py-8 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
}
